import { Component, computed, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl } from '@angular/forms';
import { HlmItemSeparator } from '@spartan-ng/helm/item';

import { LivrableCaractérisé } from '../../../../../livrables-caractérisés/model/livrable-caracterisé.model';
import { LivrableCaractériséStore } from '../../../../../livrables-caractérisés/store/livrable-caracterisé.store';
import { Caractéristique } from '../../../../../référentiel-métier/référentiel-métier-périmètre/perimetre-caracteristiques/models/caractéristique.model';
import { CaractéristiqueStore } from '../../../../../référentiel-métier/référentiel-métier-périmètre/perimetre-caracteristiques/store/caractéristique.store';
import { CheckboxSubmenuItemComponent } from '../../../../../shared/components/checkbox-submenu-item/checkbox-submenu-item.component';
import { ExpansionPanel } from '../../../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { TrierParPipe } from '../../../../../shared/pipes/trier-par.pipe';
import { FormControlNames } from '../../../../models/form-control-names.model';
import { ModuleStore } from '../../../../parcours-module/store/module.store';
import { ModuleDetailFormService } from '../../module-detail-form.service';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { I18nPluralPipe } from '@angular/common';
import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-details-livrables-caracteristiques',
  imports: [
    CheckboxSubmenuItemComponent,
    ExpansionPanel,
    HlmItemSeparator,
    TrierParPipe,
    ChipComponent,
    I18nPluralPipe,
  ],
  templateUrl: './form-details-livrables-caracteristiques.component.html',
})
export class FormDetailsLivrablesCaracteristiquesComponent implements OnInit {
  readonly #caracteristiqueStore = inject(CaractéristiqueStore);
  readonly #moduleStore = inject(ModuleStore);
  readonly #livrableCaractériséStore = inject(LivrableCaractériséStore);
  readonly #fb = inject(FormBuilder);
  readonly #destroy = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #detailFormService = inject(ModuleDetailFormService);

  readonly referentiel = input.required<Référentiel>();
  readonly livrableCaracterisé = input.required<LivrableCaractérisé>({ alias: 'livrableCaracterise' });
  readonly idLivrable = input<string>();

  readonly #moduleSelectionné = computed(() => this.#moduleStore.selectedEntity());

  readonly caracteristiques = computed(() => this.#caracteristiqueStore.caracteristiques());

  protected readonly caracteristiqueMapping: { [k: string]: string } = {
    '=0': '# sélectionnée',
    '=1': '# sélectionnée',
    other: '# sélectionnées',
  };

  form = this.#fb.group({
    [FormControlNames.SOUS_CARACTERISTIQUES]: [[]],
  });

  constructor() {
    effect(() => this.#caracteristiqueStore.charger(this.referentiel().id));

    effect(() => {
      const livrable = this.livrableCaracterisé();
      if (livrable) {
        this.sousCaracteristiques.setValue(livrable.caracteristiques, { emitEvent: false });
      } else if (this.caracteristiquesLivrableGenerique.value.length > 0) {
        this.sousCaracteristiques.setValue(this.caracteristiquesLivrableGenerique.value);
      }
    });
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroy)).subscribe(() => {
      this.submit();
      this.#sidepanelService.close();
    });
  }

  submit() {
    let livrable = this.livrableCaracterisé();
    const idLivrable = this.idLivrable();
    const module = this.#moduleSelectionné();

    // Cas pouvant arriver lorsqu'on vient de créer le livrable caractérisé
    // Si c'était le livrable générique, alors livrable === null
    if (livrable === undefined && idLivrable) livrable = this.#livrableCaractériséStore.entityMap()[idLivrable];

    if (module) {
      if (livrable) {
        const idLivrableCaractérisé = livrable.id;
        if (!idLivrableCaractérisé) return;

        this.#sidepanelService.loading.set(true);

        this.#livrableCaractériséStore.modifier({
          id: idLivrableCaractérisé,
          data: {
            idsCaracteristiques: this.sousCaracteristiques.value.map((x) => x.id),
            caractéristiques: this.sousCaracteristiques.value,
          },
          next: () => {
            this.#moduleStore.selection(module.id);
            this.#sidepanelService.loading.set(false);
          },
        });
      } else {
        // Pour gérer le cas du livrable générique
        this.caracteristiquesLivrableGenerique.setValue(this.sousCaracteristiques.value);
        this.#detailFormService.submitModule();
      }
    }
  }

  countSousCaracteristiquesSelectionnees(caracteristique: Caractéristique) {
    return caracteristique.sousCaracteristiques.filter((x) =>
      this.sousCaracteristiques.value.some((y) => x.id === y.id),
    ).length;
  }

  get sousCaracteristiques() {
    return this.form.get(FormControlNames.SOUS_CARACTERISTIQUES) as unknown as FormControl<Caractéristique[]>;
  }

  get caracteristiquesLivrableGenerique() {
    return this.#detailFormService.caracteristiques;
  }
}
